import { baseApi } from './baseApi';

export interface PaymentTypeBreakdownRequest {
  date: string;
  type?: 'weekly' | 'monthly' | 'daily';
  amcId?: string;
}

export interface PaymentTypeBreakdownResponse {
  UPICount: number;
  NetBankingCount: number;
  DebitCardCount: number;
  EnachCount: number;
  UAutoCount: number;
  UPIAmount: number;
  NetBankingAmount: number;
  DebitCardAmount: number;
  EnachAmount: number;
  UAutoAmount: number;
}

export interface WeeklyPaymentMetrics {
  TimePeriod: string;
  TotalTransactions: number;
  SuccessfulTransactions: number;
  FailedTransactions: number;
  SuccessRate: number;
  FailedTransactionRate: number;
  ProcessedAmount: number;
  AverageTicketSize: number;
}

export interface Transaction {
  id: string;
  ihNoOrCart: number;
  status: string;
  gateway: string;
  gatewayClicked: string;
  acknowledgedAt: string;
  timeDifferenceMins: number;
}

export interface TransactionSummaryResponse {
  stats: {
    totalQueryApi: number;
    totalNonQueryApi: number;
    totalAnalyzed: number;
    queryApiSuccessful: number;
    queryApiFailed: number;
    nonQueryApiSuccessful: number;
    nonQueryApiFailed: number;
  };
}

export interface TransactionDetail {
  id: string;
  ihNoOrCart: number;
  status: string;
  gateway: string;
  amcId: string;
  amcName: string;
  gatewayClicked: string;
  acknowledgedAt: string;
  acknowledgedBy: string | null;
  timeDifferenceMins: number;
  transactionType: string;
  amount: number;
  message?: string;
  swiftFlowErrorCode?: string;
  bank_ref_no?: string | null;
  gatewayMessage: string|null;
}

export interface DetailedTransactionSummaryResponse {
  queryApi: {
    successfulTransactions: TransactionDetail[];
    failedTransactions: TransactionDetail[];
  };
  nonQueryApi: {
    successfulTransactions: TransactionDetail[];
    failedTransactions: TransactionDetail[];
  };
  allTransactions?: TransactionDetail[];
  meta?: {
    total: number;
    page: number;
    limit: number;
  };
}

export interface PaymentMetricChanges {
  TotalTransactionsChange: number;
  SuccessfulTransactionsChange: number;
  FailedTransactionsChange: number;
  SuccessRateChange: number;
  FailedTransactionRateChange: number;
  ProcessedAmountChange: number;
  AverageTicketSizeChange: number;
}

export interface ExportTransactionSummaryRequest {
  amcId?: string;
  // Common properties
  date?: string;
  type?: 'daily' | 'weekly' | 'monthly' | '1W' | '1M' | '6M' | '1Y' | 'All';

  // For payment aggregator exports
  paymentAggregator?: string;

  // For gateway-specific exports
  gateway?: string;
}

export interface TransactionCountStatusRequest {
  date: string;
  type?: 'weekly' | 'monthly' | 'daily';
  amcId?: string;
}

export interface TransactionCountStatusResponse {
  SuccessCount: number;
  FailureCount: number;
  PendingCount: number;
  TotalCount: number;
}

export interface TransactionCountPaymodeRequest {
  date: string;
  type?: 'weekly' | 'monthly' | 'daily';
  amcId?: string;
}

export interface TransactionCountPaymodeResponse {
  UPICount: number;
  NetBankingCount: number;
  DebitCardCount: number;
  EnachCount: number;
  UAutoCount: number;
  TotalCount: number;
}

export interface AggregatorTransactionCountRequest {
  date: string;
  type?: 'weekly' | 'monthly' | 'daily';
  amcId?: string;
}

export interface AggregatorTransactionCountResponse {
  [key: string]: number;
  TotalCount: number;
}

export interface AggregatorTypeBreakdownRequest {
  date: string;
  type?: 'weekly' | 'monthly' | 'daily';
  amcId?: string;
}

export interface AggregatorTypeBreakdownResponse {
  [key: string]: number; // Dynamic gateway counts and amounts
  // Format: {gatewayName}Count and {gatewayName}Amount
}

export interface AssetBreakdownItem {
  assetId: string;
  assetName: string;
  transactionType: string | null;
  totalTransactions: number;
  successCount: number;
  failureCount: number;
  pendingCount: number;
  totalAmount: number;
}

export interface TimeBasedTransactionItem {
  timeSlot: string;
  startTime: string;
  endTime: string;
  lumpsumCount: number;
  mandateCount: number;
  totalAmount: number;
}

export interface TransactionCountStatus {
  Year?: string;
  Month?: string;
  MonthName?: string;
  TransactionDate?: string;
  SuccessCount: number;
  FailureCount: number;
  PendingCount: number;
}

export interface TransactionCountPaymode {
  Year?: string;
  Month?: string;
  MonthName?: string;
  TransactionDate?: string;
  UPISuccessCount: number;
  NetBankingSuccessCount: number;
  DebitCardSuccessCount: number;
  EnachSuccessCount: number;
  UAutoSuccessCount: number;
  TotalSuccessCount: number;
}
export interface AmcListResponse {
  id: string;
  name: string;
  fundCode: string;
  useExternalUpiWebhook: boolean;
  logoPath: string | null;
  isRTA: boolean;
  isActive: boolean;
  safeSelect: any; // Update this to the correct type if known
}

export interface RetryMetricsResponse {
  recoveredTransactions: number;
  recoveryRate: number;
  recoveredValue: number;
  retrySuccessContribution: number;
  totalRetryAttempts: number;
  totalSuccessful: number;
}
// Update the tagTypes in baseApi.ts to include these new tag types
export const dashboardApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Original endpoint
    getPaymentTypeBreakdown: builder.query<
      PaymentTypeBreakdownResponse,
      PaymentTypeBreakdownRequest
    >({
      query: (params) => ({
        method: 'GET',
        url: `/dashboard/payment-type-breakdown`,
        params: {
          date: params.date,
          type: params.type,
          ...(params.amcId && { amcId: params.amcId }),
        },
      }),
      providesTags: ['PaymentType'],
      transformResponse: (response: any) => {
        return {
          ...response,
          NetBankingAmount: response.NetBankingAmount || 0,
          DebitCardAmount: response.DebitCardAmount || 0,
          EnachAmount: response.EnachAmount || 0,
          UAutoAmount: response.UAutoAmount || 0,
          UPIAmount: response.UPIAmount || 0,
        };
      },
    }),

    getAggregatorTypeBreakdown: builder.query<
      AggregatorTypeBreakdownResponse,
      AggregatorTypeBreakdownRequest
    >({
      query: (params) => ({
        method: 'GET',
        url: `/dashboard/aggregator-type-breakdown`,
        params: {
          date: params.date,
          type: params.type,
          ...(params.amcId && { amcId: params.amcId }),
        },
      }),
      providesTags: ['Aggregator'],
      transformResponse: (response: any) => {
        // Ensure all amount fields default to 0 if missing
        const result: AggregatorTypeBreakdownResponse = { ...response };
        Object.keys(response).forEach((key) => {
          if (key.endsWith('Amount') && !response[key]) {
            result[key] = 0;
          }
        });
        return result;
      },
    }),

    getTransactionCountStatus: builder.query<
      TransactionCountStatus[],
      { type?: '1W' | '1M' | '6M' | '1Y' | 'All'; amcId?: string }
    >({
      query: (params) => ({
        method: 'GET',
        url: `/dashboard/transaction-count-status`,
        params: {
          type: params.type,
          ...(params.amcId && { amcId: params.amcId }),
        },
      }),
      providesTags: ['Transaction'],
    }),

    getTransactionCountPaymode: builder.query<
      TransactionCountPaymode[],
      { type?: '1W' | '1M' | '6M' | '1Y' | 'All'; gateway?: string; amcId?: string }
    >({
      query: (params) => ({
        method: 'GET',
        url: `/dashboard/transaction-count-paymode`,
        params: {
          type: params.type,
          gateway: params.gateway,
          ...(params.amcId && { amcId: params.amcId }),
        },
      }),
      providesTags: ['Transaction'],
    }),

    getPaymentSuccessRateComparison: builder.query<
      {
        currentWeek: WeeklyPaymentMetrics;
        previousWeek: WeeklyPaymentMetrics;
        changes: PaymentMetricChanges;
      },
      { paymentAggregator?: string; amcId?: string; type?: '1W' | '1M' | '6M' | '1Y' | 'All' }
    >({
      query: ({ paymentAggregator, amcId, type }) => ({
        method: 'GET',
        url: `/dashboard/weekly-comparison`,
        params: {
          ...(paymentAggregator && { paymentAggregator }),
          ...(amcId && { amcId }),
          ...(type && { type }),
        },
      }),
      providesTags: ['Payment'],
    }),

    getAssetBreakdown: builder.query<
      AssetBreakdownItem[],
      { amcId?: string; type?: '1W' | '1M' | '6M' | '1Y' | 'All' }
    >({
      query: (params = {}) => ({
        method: 'GET',
        url: `/dashboard/asset-breakdown`,
        params: {
          ...(params.amcId && { amcId: params.amcId }),
          ...(params.type && { type: params.type }),
        },
      }),
      providesTags: ['Transaction'],
    }),

    getTimeBasedTransactions: builder.query<
      TimeBasedTransactionItem[],
      { date: string; interval?: number; amcId?: string }
    >({
      query: ({ date, interval = 1, amcId }) => ({
        method: 'GET',
        url: `/dashboard/transactions/time-based`,
        params: {
          date,
          interval,
          ...(amcId && { amcId }),
        },
      }),
      providesTags: ['Transaction'],
    }),
    getTransactionSummary: builder.query<
      TransactionSummaryResponse,
      { amcId?: string; date?: string }
    >({
      query: (params = {}) => ({
        method: 'GET',
        url: `/dashboard/transaction-summary`,
        params: {
          ...(params.amcId && { amcId: params.amcId }),
          ...(params.date && { date: params.date }),
        },
      }),
      providesTags: ['Transaction'],
    }),
    getTransactionTimeDifference: builder.query<
      DetailedTransactionSummaryResponse,
      {
        amc?: string;
        date?: string;
        startDate?: string;
        endDate?: string;
        transactionType?: string;
        paymentAggregator?: string;
        status?: string;
        acknowledgedBy?: string | null;
        page?: number;
        limit?: number;
        search?: string;
      }
    >({
      query: (params = {}) => {
        // Filter out undefined values
        const cleanParams: any = {};
        if (params.date) cleanParams.date = params.date;
        if (params.startDate) cleanParams.startDate = params.startDate;
        if (params.endDate) cleanParams.endDate = params.endDate;
        if (params.amc) cleanParams.amc = params.amc;
        if (params.transactionType) cleanParams.transactionType = params.transactionType;
        if (params.paymentAggregator) cleanParams.paymentAggregator = params.paymentAggregator;
        if (params.status) cleanParams.status = params.status;
        if (params.acknowledgedBy !== undefined) cleanParams.acknowledgedBy = params.acknowledgedBy;
        if (params.page !== undefined) cleanParams.page = params.page;
        if (params.limit !== undefined) cleanParams.limit = params.limit;
        if (params.search) cleanParams.search = params.search;

        return {
          method: 'GET',
          url: `/dashboard/transaction-time-difference`,
          params: cleanParams,
        };
      },
      providesTags: ['Transaction'],
    }),

    exportTransactionTimeDifference: builder.mutation<
      Blob,
      {
        amc?: string;
        date?: string;
        startDate?: string;
        endDate?: string;
        transactionType?: string;
        paymentAggregator?: string;
        status?: string;
        acknowledgedBy?: string | null;
        search?: string;
        page?: number;
        limit?: number;
      }
    >({
      query: (params = {}) => {
        const cleanParams: any = {};
        if (params.date) cleanParams.date = params.date;
        if (params.startDate) cleanParams.startDate = params.startDate;
        if (params.endDate) cleanParams.endDate = params.endDate;
        if (params.amc) cleanParams.amc = params.amc;
        if (params.transactionType) cleanParams.transactionType = params.transactionType;
        if (params.paymentAggregator) cleanParams.paymentAggregator = params.paymentAggregator;
        if (params.status) cleanParams.status = params.status;
        if (params.acknowledgedBy !== undefined) cleanParams.acknowledgedBy = params.acknowledgedBy;
        if (params.search) cleanParams.search = params.search;
        if (params.page !== undefined) cleanParams.page = params.page;
        if (params.limit !== undefined) cleanParams.limit = params.limit;

        return {
          method: 'GET',
          url: `/dashboard/export-transaction-time-difference`,
          params: cleanParams,
          responseHandler: (response) => response.blob(),
        };
      },
    }),

    exportTransactionSummary: builder.mutation<Blob, ExportTransactionSummaryRequest>({
      query: (params) => {
        const cleanParams: any = {};
        if (params.amcId) cleanParams.amcId = params.amcId;

        return {
          url: '/dashboard/export-transactions',
          method: 'GET',
          params: cleanParams,
          responseHandler: (response: Response) => response.blob(),
        };
      },
    }),

    exportTransactionStats: builder.mutation<Blob, ExportTransactionSummaryRequest>({
      query: (params) => {
        const cleanParams: any = {};
        if (params.amcId) cleanParams.amcId = params.amcId;
        if (params.date) cleanParams.date = params.date;
        if (params.type) cleanParams.type = params.type;
        if (params.paymentAggregator) cleanParams.paymentAggregator = params.paymentAggregator;
        if (params.gateway) cleanParams.gateway = params.gateway;

        return {
          url: '/dashboard/export-transaction-stats',
          method: 'GET',
          params: cleanParams,
          responseHandler: (response: Response) => response.blob(),
        };
      },
    }),

    exportAggregatorBreakdown: builder.mutation<Blob, ExportTransactionSummaryRequest>({
      query: (params) => {
        const cleanParams: any = {};
        if (params.amcId) cleanParams.amcId = params.amcId;
        if (params.date) cleanParams.date = params.date;
        if (params.type) cleanParams.type = params.type;

        return {
          url: '/dashboard/export-aggregator-breakdown',
          method: 'GET',
          params: cleanParams,
          responseHandler: (response: Response) => response.blob(),
        };
      },
    }),

    exportPaymentBreakdown: builder.mutation<Blob, ExportTransactionSummaryRequest>({
      query: (params) => {
        const cleanParams: any = {};
        if (params.amcId) cleanParams.amcId = params.amcId;
        if (params.date) cleanParams.date = params.date;
        if (params.type) cleanParams.type = params.type;

        return {
          url: '/dashboard/export-payment-breakdown',
          method: 'GET',
          params: cleanParams,
          responseHandler: (response: Response) => response.blob(),
        };
      },
    }),

    exportPaymentSuccessComparison: builder.mutation<
      Blob,
      { paymentAggregator: string; amcId?: string; type?: '1W' | '1M' | '6M' | '1Y' | 'All' }
    >({
      query: (params) => {
        const cleanParams: any = {
          paymentAggregator: params.paymentAggregator,
        };

        if (params.amcId) cleanParams.amcId = params.amcId;
        if (params.type) cleanParams.type = params.type;

        return {
          url: '/dashboard/export-payment-success-comparison',
          method: 'GET',
          params: cleanParams,
          responseHandler: (response: Response) => response.blob(),
        };
      },
    }),

    exportAssetTransactions: builder.mutation<
      Blob,
      { amcId?: string; type: '1W' | '1M' | '6M' | '1Y' | 'All' }
    >({
      query: (params) => ({
        url: '/dashboard/export-asset-wise-stats',
        method: 'GET',
        params: {
          ...(params.amcId && { amcId: params.amcId }),
          ...(params.type && { type: params.type }),
        },
        responseHandler: (response: Response) => {
          if (!response.ok) {
            return response
              .json()
              .then((err) => {
                console.error('Export error response:', err);
                throw new Error(err.message || 'Export failed');
              })
              .catch(() => {
                throw new Error('Failed to parse error response');
              });
          }
          return response.blob();
        },
      }),
    }),

    exportPaymentTypeSuccessRate: builder.mutation<Blob, ExportTransactionSummaryRequest>({
      query: (params) => {
        const cleanParams: any = {};
        if (params.amcId) cleanParams.amcId = params.amcId;
        if (params.gateway) cleanParams.gateway = params.gateway;
        if (params.date) cleanParams.date = params.date;
        if (params.type) cleanParams.type = params.type;

        return {
          url: '/dashboard/export-payment-type-success-rate',
          method: 'GET',
          params: cleanParams,
          responseHandler: (response: Response) => response.blob(),
        };
      },
    }),

    getRetryMetrics: builder.query<
      RetryMetricsResponse,
      { date?: string; type?: 'daily' | 'weekly' | 'monthly'; amcId?: string; assetId?: string }
    >({
      query: (params = {}) => ({
        method: 'GET',
        url: `/dashboard/retry-metrics`,
        params: {
          ...(params.date && { date: params.date }),
          ...(params.type && { type: params.type }),
          ...(params.amcId && { amcId: params.amcId }),
          ...(params.assetId && { assetId: params.assetId }),
        },
      }),
      providesTags: ['Transaction'],
    }),

    exportRetryMetrics: builder.mutation<
      Blob,
      { date?: string; type?: 'daily' | 'weekly' | 'monthly'; amcId?: string; assetId?: string }
    >({
      query: (params = {}) => ({
        method: 'GET',
        url: `/dashboard/export-retry-metrics`,
        params: {
          ...(params.date && { date: params.date }),
          ...(params.type && { type: params.type }),
          ...(params.amcId && { amcId: params.amcId }),
          ...(params.assetId && { assetId: params.assetId }),
        },
        responseHandler: (response: Response) => response.blob(),
      }),
    }),

    getAmcList: builder.query<AmcListResponse[], void>({
      query: () => ({
        url: '/amcs/list',
        method: 'GET',
      }),
      providesTags: ['AMC'],
    }),
  }),
});

export const {
  useGetPaymentTypeBreakdownQuery,
  useGetAggregatorTypeBreakdownQuery,
  useExportPaymentSuccessComparisonMutation,
  useGetTransactionCountPaymodeQuery,
  useExportAggregatorBreakdownMutation,
  useExportPaymentBreakdownMutation,
  useExportAssetTransactionsMutation,
  useExportPaymentTypeSuccessRateMutation,
  useGetPaymentSuccessRateComparisonQuery,
  useGetTransactionCountStatusQuery,
  useGetAssetBreakdownQuery,
  useGetTimeBasedTransactionsQuery,
  useGetTransactionSummaryQuery,
  useGetTransactionTimeDifferenceQuery,
  useExportTransactionTimeDifferenceMutation,
  useExportTransactionStatsMutation,
  useExportTransactionSummaryMutation,
  useGetAmcListQuery,
  useGetRetryMetricsQuery,
  useExportRetryMetricsMutation,
} = dashboardApi;
